/**
 * RootCauseChainDisplay Component
 * Displays the causal chain from Snapshot V2 analysis
 * PLA-138 Story C
 */

import { ArrowDown } from 'lucide-react';

interface RootCauseChainItem {
  flaw_id: string;
  position_in_chain: number;
  causal_relationship: 'root_cause' | 'enabler' | 'compensator' | 'symptom';
  evidence: string;
}

interface RootCauseChainDisplayProps {
  chain: RootCauseChainItem[];
}

const relationshipColors: Record<string, string> = {
  root_cause: 'bg-red-100 text-red-800 border-red-200',
  enabler: 'bg-orange-100 text-orange-800 border-orange-200',
  compensator: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  symptom: 'bg-blue-100 text-blue-800 border-blue-200',
};

const relationshipLabels: Record<string, string> = {
  root_cause: 'Root Cause',
  enabler: 'Enabler',
  compensator: 'Compensator',
  symptom: 'Symptom',
};

export function RootCauseChainDisplay({ chain }: RootCauseChainDisplayProps) {
  if (!chain || chain.length === 0) {
    return null;
  }

  // Sort by position in chain
  const sortedChain = [...chain].sort((a, b) => a.position_in_chain - b.position_in_chain);

  return (
    <div className="space-y-2 py-3">
      {sortedChain.map((item, index) => (
        <div key={`${item.flaw_id}-${index}`}>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
              {item.position_in_chain}
            </div>
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">
                  {item.flaw_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${relationshipColors[item.causal_relationship] || 'bg-gray-100 text-gray-800'}`}>
                  {relationshipLabels[item.causal_relationship] || item.causal_relationship}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{item.evidence}</p>
            </div>
          </div>
          {index < sortedChain.length - 1 && (
            <div className="flex justify-start ml-2.5 py-1">
              <ArrowDown className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
